import { BrowserRouter as Router, Routes, Route, Outlet } from "react-router";
import AppLayout from "./layout/AppLayout";
import { ScrollToTop } from "./shared/ui/common/ScrollToTop";

import { AuthProvider } from "./modules/auth/application/AuthProvider";
import { RecordingActivityProvider } from "./modules/auth/application/RecordingActivityProvider";
import ProtectedRoute from "./modules/auth/ui/components/ProtectedRoute";
import { TherapistProvider } from "./context/TherapistProvider";
import SubscriptionRouteGuard from "./modules/subscription/ui/components/SubscriptionRouteGuard";
import CookieConsentBanner from "./shared/ui/gdpr/CookieConsentBanner";
import { SidebarProvider } from "./context/SidebarProvider";

import NotFound from "./modules/dashboard/ui/pages/NotFound";
import Blank from "./modules/dashboard/ui/pages/Blank";
import Home from "./modules/dashboard/ui/pages/Home";
import UserProfile from "./modules/user_profile/ui/pages/UserProfile";
import SessionConfig from "./modules/session_config/ui/pages/SessionConfig";
import PracticeSettingsRedirect from "./shared/navigation/PracticeSettingsRedirect";
import Support from "./modules/support/ui/pages/Support";
import PrivacyPolicy from "./modules/legal/ui/pages/PrivacyPolicy";
import TermsOfService from "./modules/legal/ui/pages/TermsOfService";

import { authRoutes } from "./modules/auth/routes";
import { guestMeetingRoutes, meetingRoutes, sessionRoutes } from "./modules/session/routes";
import { patientRoutes } from "./modules/patient/routes";
import { patientRecordRoutes } from "./modules/patient_record/routes";
import { therapeuticPlanRoutes } from "./modules/therapeutic_plan/routes";
import { subscriptionRoutes } from "./modules/subscription/routes";
import { marketplaceRoutes } from "./modules/marketplace/routes";
import { meditationRoutes } from "./modules/meditation/routes";
import {
  constellationMeetingRoutes,
  constellationPublicRoutes,
  constellationRoutes,
} from "./modules/constellation/routes";
import { emdrBoardPublicRoutes, emdrBoardRoutes } from "./modules/emdr/routes";
import {
  systemicMapMeetingRoutes,
  systemicMapPublicRoutes,
  systemicMapRoutes,
} from "./modules/systemic_map/routes";
import { publicDirectoryRoutes } from "./modules/public_directory/routes";
import { publicMeditationRoutes } from "./modules/public-meditations/routes";
import {
  schedulerMeetingRoutes,
  schedulerPublicRoutes,
  schedulerRoutes,
} from "./modules/scheduler/routes";
import { paymentsRoutes } from "./modules/payments/routes";
import { reportsRoutes } from "./modules/reports/routes";
import { renderPortalRoutes } from "./modules/patient_portal/routes";
import {
  centerAdminRoutes,
  centerMemberRoutes,
  centerPublicRoutes,
} from "./modules/center_admin/routes";

export default function App() {
  return (
    <RecordingActivityProvider>
      <AuthProvider>
        <TherapistProvider>
        <Router>
          <ScrollToTop />
          <CookieConsentBanner />
          <Routes>

            {/* PROTECTED ROUTES — inside AppLayout */}
            <Route
              element={
                <ProtectedRoute>
                  <AppLayout />
                </ProtectedRoute>
              }
            >
              <Route index path="/" element={<Home />} />
              <Route path="/blank" element={<Blank />} />
              <Route path="/settings" element={<SessionConfig />} />
              {/* Where each tool kept its settings before they moved into
                  practice settings. Still reached from outside: bookmarks, and
                  the backend sends Stripe's OAuth outcome to
                  /payments/settings?stripe=…, which the redirect carries on. A
                  static path, so it outranks /payments/:id. */}
              <Route
                path="/scheduler/settings"
                element={<PracticeSettingsRedirect tab="scheduler" />}
              />
              <Route
                path="/payments/settings"
                element={<PracticeSettingsRedirect tab="payments" />}
              />
              <Route path="/support" element={<Support />} />
              <Route path="/profile" element={<UserProfile />} />

              {/* A therapist's own centre business — membership, not admin */}
              {centerMemberRoutes.map((r) => (
                <Route key={r.path as string} path={r.path as string} element={r.element} />
              ))}

              {/* CENTRE ADMINISTRATION — guarded by membership, not by plan */}
              {centerAdminRoutes.map((r, index) => (
                <Route key={`center-admin-${index}`} element={r.element}>
                  {(r.children ?? []).map((child) => (
                    <Route
                      key={child.path as string}
                      path={child.path as string}
                      element={child.element}
                    />
                  ))}
                </Route>
              ))}

              {/* Subscription-gated routes */}
              <Route element={<SubscriptionRouteGuard />}>
                {therapeuticPlanRoutes.map((r) => (
                  <Route key={r.path as string} path={r.path as string} element={r.element} />
                ))}
              </Route>

              {sessionRoutes.map((r) => (
                <Route key={r.path as string} path={r.path as string} element={r.element} />
              ))}
              {patientRoutes.map((r) => (
                <Route key={r.path as string} path={r.path as string} element={r.element} />
              ))}
              {patientRecordRoutes.map((r) => (
                <Route key={r.path as string} path={r.path as string} element={r.element} />
              ))}
              {marketplaceRoutes.map((r) => (
                <Route key={r.path as string} path={r.path as string} element={r.element} />
              ))}
              {meditationRoutes.map((r) => (
                <Route key={r.path as string} path={r.path as string} element={r.element} />
              ))}
              {systemicMapRoutes.map((r) => (
                <Route key={r.path as string} path={r.path as string} element={r.element} />
              ))}
              {constellationRoutes.map((r) => (
                <Route key={r.path as string} path={r.path as string} element={r.element} />
              ))}
              {schedulerRoutes.map((r) => (
                <Route key={r.path as string} path={r.path as string} element={r.element} />
              ))}
              {paymentsRoutes.map((r) => (
                <Route key={r.path as string} path={r.path as string} element={r.element} />
              ))}
              {reportsRoutes.map((r) => (
                <Route key={r.path as string} path={r.path as string} element={r.element} />
              ))}
            </Route>

            {/* MEETING ROUTES — protected, but with no app chrome at all */}
            <Route
              element={
                <ProtectedRoute>
                  <Outlet />
                </ProtectedRoute>
              }
            >
              {[...meetingRoutes, ...schedulerMeetingRoutes, ...emdrBoardRoutes].map((r) => (
                <Route key={r.path as string} path={r.path as string} element={r.element} />
              ))}
            </Route>

            {/* TOOL BOARDS FRAMED INSIDE A CALL — protected, no app chrome.
                The boards borrow the sidebar's width on entry, so they still
                need its provider even with no sidebar to borrow from. */}
            <Route
              element={
                <ProtectedRoute>
                  <SidebarProvider>
                    <Outlet />
                  </SidebarProvider>
                </ProtectedRoute>
              }
            >
              {[...constellationMeetingRoutes, ...systemicMapMeetingRoutes].map((r) => (
                <Route key={r.path as string} path={r.path as string} element={r.element} />
              ))}
            </Route>

            {/* SUBSCRIPTION STANDALONE ROUTES */}
            <Route
              element={
                <ProtectedRoute>
                  <Outlet />
                </ProtectedRoute>
              }
            >
              {subscriptionRoutes.map((r) => (
                <Route key={r.path as string} path={r.path as string} element={r.element} />
              ))}
            </Route>

            {/* GUEST MEETING — the patient's way in, with no account at all */}
            {guestMeetingRoutes.map((r) => (
              <Route key={r.path as string} path={r.path as string} element={r.element} />
            ))}
            {/* EMDR BOARD — the patient's screen of a board, from its link */}
            {emdrBoardPublicRoutes.map((r) => (
              <Route key={r.path as string} path={r.path as string} element={r.element} />
            ))}

            {/* PUBLIC ROUTES */}
            {authRoutes.map((r) => (
              <Route key={r.path as string} path={r.path as string} element={r.element} />
            ))}
            {centerPublicRoutes.map((r) => (
              <Route key={r.path as string} path={r.path as string} element={r.element} />
            ))}
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsOfService />} />

            {/* PUBLIC DIRECTORY — anonymous, no auth, no app chrome */}
            {publicDirectoryRoutes.map((r) => (
              <Route key={r.path as string} path={r.path as string} element={r.element} />
            ))}

            {/* PUBLIC MEDITATION LIBRARY — Alchely-curated, no auth */}
            {publicMeditationRoutes.map((r) => (
              <Route key={r.path as string} path={r.path as string} element={r.element} />
            ))}

            {/* PUBLIC SYSTEMIC MAP (patient, token-scoped, no auth) */}
            {systemicMapPublicRoutes.map((r) => (
              <Route key={r.path as string} path={r.path as string} element={r.element} />
            ))}

            {/* PUBLIC CONSTELLATION (patient, token-scoped, no auth) */}
            {constellationPublicRoutes.map((r) => (
              <Route key={r.path as string} path={r.path as string} element={r.element} />
            ))}

            {/* PATIENT PORTAL ROUTES */}
            {renderPortalRoutes()}

            {schedulerPublicRoutes.map((r) => (
              <Route key={r.path as string} path={r.path as string} element={r.element} />
            ))}

            {/* 404 */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Router>
        </TherapistProvider>
      </AuthProvider>
    </RecordingActivityProvider>
  );
}
